import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import './TagTester.css';

interface ScanReport {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  mode: string;
  riskLevel: string;
  riskScore: number;
  executionEvidence?: string;
  scannerConfidence?: "low" | "medium" | "high";
  createdAt: string;
  completedAt?: string;
  artifact: {
    id: string;
    sourceType: string;
    rawCode: string;
    entryUrl?: string;
    vendorGuess?: string;
    vendorConfidence?: number;
    detectedMacros: Array<{
      macroType: string;
      rawToken: string;
      aliasMatched?: string;
      context?: string;
      isExpected?: boolean;
      isMissing?: boolean;
      isMalformed?: boolean;
      syntheticValue?: string;
      surroundingSnippet?: string;
    }>;
    sizeHints: Record<string, unknown>;
    clickUrlCandidates: string[];
    extractedUrls: Array<{
      url: string;
      host: string;
      pathname: string;
      sourceContext: string;
    }>;
    scripts: string[];
    createdAt: string;
  };
  riskFindings: Array<{
    id: string;
    category?: string;
    severity: string;
    title: string;
    detail: string;
    evidence?: string;
    weight: number;
  }>;
  vendorMatches?: Array<{
    vendorId: string;
    vendorName: string;
    confidence: number;
    matchedPatterns: string[];
  }>;
  vastAnalysis?: {
    wrapperChain: string[];
    mediaFiles: Array<{ url: string; type: string; bitrate?: number }>;
    vpaidDetected: boolean;
    simidDetected: boolean;
    omidDetected: boolean;
  };
  runtimeTrace: {
    domSnapshots: unknown[];
    networkRequests: Array<{
      url: string;
      method: string;
      status: number;
      resourceType: string;
      contentLength: number;
      timing: number;
      blocked?: boolean;
      blockReason?: string;
    }>;
    redirectChain: string[];
    consoleMessages: Array<{ level: string; text: string }>;
    errors: string[];
    frameTree: unknown[];
    cookiesSet: Array<{ name: string; domain: string; secure: boolean; httpOnly: boolean; sameSite: string }>;
    storageAccess: string[];
    popupAttempts: number;
    navigationAttempts: number;
    executionTimeMs: number;
    limitViolations?: string[];
  };
}

export function TagTesterResults(): JSX.Element {
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ScanReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!scanId) return;

    const fetchReport = async () => {
      try {
        const response = await fetch(apiUrl(`/tag-scans/${scanId}`));
        if (!response.ok) throw new Error('Failed to fetch scan report');

        const data = await response.json();
        setReport(data.data);

        if (data.data.status === 'queued' || data.data.status === 'running') {
          setTimeout(fetchReport, 2000);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [scanId]);

  if (loading || (report && (report.status === 'queued' || report.status === 'running'))) {
    return (
      <div className="tag-tester-results">
        <div className="loading-state">
          <div className="spinner" />
          <h2>Scanning tag...</h2>
          <p>Status: {report?.status || 'connecting'}</p>
          <div className="progress-bar">
            <div className="progress-fill" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="tag-tester-results">
        <div className="error-state">
          <h2>Scan Error</h2>
          <p>{error || 'Unable to load scan results'}</p>
          <button className="back-button" onClick={() => navigate('/tag-tester')}>
            Back to Intake
          </button>
        </div>
      </div>
    );
  }

  const toggleFinding = (id: string) => {
    const newExpanded = new Set(expandedFindings);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedFindings(newExpanded);
  };

  const findings = report.riskFindings || [];
  const groupedFindings = findings.reduce((acc: Record<string, typeof findings>, finding) => {
    const cat = finding.category || 'general';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(finding);
    return acc;
  }, {});

  const macros = report.artifact.detectedMacros || [];
  const networkRequests = report.runtimeTrace?.networkRequests || [];
  const consoleMessages = report.runtimeTrace?.consoleMessages || [];
  const runtimeErrors = report.runtimeTrace?.errors || [];
  const limitViolations = report.runtimeTrace?.limitViolations || [];
  const cookiesSet = report.runtimeTrace?.cookiesSet || [];
  const storageAccess = report.runtimeTrace?.storageAccess || [];
  const blockedCount = networkRequests.filter(r => r.blocked).length;
  const allowedCount = networkRequests.filter(r => !r.blocked).length;
  const totalBytes = networkRequests.reduce((sum, r) => sum + (r.contentLength || 0), 0);

  const duration = report.runtimeTrace?.executionTimeMs
    ? (report.runtimeTrace.executionTimeMs / 1000).toFixed(1)
    : report.completedAt && report.createdAt
    ? ((new Date(report.completedAt).getTime() - new Date(report.createdAt).getTime()) / 1000).toFixed(1)
    : '\u2014';

  return (
    <div className="tag-tester-results">
      <div className="results-header">
        <button className="back-button" onClick={() => navigate('/tag-tester')}>
          \u2190 Back
        </button>
        <div className="header-title">
          <h1>Scan Results</h1>
          <p className="scan-label">ID: {report.id.slice(0, 8)}...</p>
        </div>
      </div>

      <div className="results-container">
        {/* Summary Card */}
        <div className="summary-card">
          <div className="summary-left">
            <div className={`risk-badge risk-${report.riskLevel}`}>
              {report.riskLevel.charAt(0).toUpperCase() + report.riskLevel.slice(1)}
            </div>
            {report.scannerConfidence && (              <div className={`confidence-badge confidence-${report.scannerConfidence}`}>                <span className="confidence-label">Scanner Confidence</span>                <span className="confidence-value">{report.scannerConfidence.charAt(0).toUpperCase() + report.scannerConfidence.slice(1)}</span>              </div>            )}
            <div className="summary-info">
              <div className="score-display">
                <span className="score-number">{report.riskScore}</span>
                <span className="score-label">Risk Score</span>
              </div>
            </div>
          </div>
          <div className="summary-middle">
            <div className="summary-stat">
              <span className="stat-label">Source Type</span>
              <span className="stat-value">{report.artifact.sourceType}</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Duration</span>
              <span className="stat-value">{duration}s</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Mode</span>
              <span className="stat-value">{report.mode}</span>
            </div>
            {report.executionEvidence && (
              <div className="summary-stat">
                <span className="stat-label">Evidence</span>
                <span className="stat-value">{report.executionEvidence.replace(/_/g, ' ')}</span>
              </div>
            )}
          </div>
          <div className="summary-right">
            {report.artifact.vendorGuess && (
              <div className="vendors-quick">
                <span className="label">Vendor Detected</span>
                <div className="vendor-badges">
                  <span className="vendor-badge">{report.artifact.vendorGuess}</span>
                  {report.artifact.vendorConfidence != null && (
                    <span className="vendor-confidence">
                      {Math.round(report.artifact.vendorConfidence * 100)}%
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Limit Violations Warning */}
        {limitViolations.length > 0 && (
          <div className="limit-violations-banner">
            <strong>Resource Limits Exceeded</strong>
            <p>This tag exceeded the following limits: {limitViolations.join(', ')}</p>
          </div>
        )}

        {/* Risk Findings */}
        {Object.keys(groupedFindings).length > 0 && (
          <div className="findings-section">
            <h2>Risk Findings ({findings.length})</h2>
            {Object.entries(groupedFindings).map(([category, catFindings]) => (
              <div key={category} className="findings-group">
                <h3 className="group-title">
                  {category
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </h3>
                {catFindings.map((finding) => (
                  <div
                    key={finding.id}
                    className={`finding-item finding-${finding.severity}`}
                    onClick={() => toggleFinding(finding.id)}
                  >
                    <div className="finding-header">
                      <span className={`severity-icon severity-${finding.severity}`} />
                      <span className="finding-description">
                        {finding.title || finding.detail}
                      </span>
                      <span className="finding-weight">w={finding.weight}</span>
                      <span className="expand-icon">
                        {expandedFindings.has(finding.id) ? '\u25BC' : '\u25B6'}
                      </span>
                    </div>
                    {expandedFindings.has(finding.id) && (
                      <div className="finding-evidence">
                        {finding.detail && <p>{finding.detail}</p>}
                        {finding.evidence && <code>{finding.evidence}</code>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {findings.length === 0 && (
          <div className="findings-section">
            <h2>Risk Findings</h2>
            <div className="empty-findings">
              <p>No risk findings detected</p>
            </div>
          </div>
        )}

        {/* Vendor Detection */}
        {report.vendorMatches && report.vendorMatches.length > 0 && (
          <div className="vendors-section">
            <h2>Vendor Detection</h2>
            <div className="vendors-list">
              {report.vendorMatches.map((vendor, idx) => (
                <div key={idx} className="vendor-row">
                  <span className="vendor-name">{vendor.vendorName}</span>
                  <div className="confidence-bar">
                    <div
                      className="confidence-fill"
                      style={{ width: `${vendor.confidence * 100}%` }}
                    />
                  </div>
                  <span className="confidence-text">{Math.round(vendor.confidence * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Macro Analysis */}
        {macros.length > 0 && (
          <div className="macros-section">
            <h2>Macro Analysis ({macros.length})</h2>
            <div className="macros-list">
              {macros.map((macro, idx) => (
                <div key={idx} className={`macro-card ${macro.isExpected ? 'macro-expected' : ''} ${macro.isMissing ? 'macro-missing' : ''} ${macro.isMalformed ? 'macro-malformed' : ''}`}>
                  <div className="macro-header">
                    <span className="macro-name">{macro.rawToken}</span>
                    {macro.isExpected && <span className="macro-badge badge-expected">Expected</span>}
                    {macro.isMissing && <span className="macro-badge badge-missing">Missing</span>}
                    {macro.isMalformed && <span className="macro-badge badge-malformed">Malformed</span>}
                  </div>
                  <div className="macro-context">
                    Type: {macro.macroType}
                    {macro.context && <> | Found in: {macro.context}</>}
                  </div>
                  {macro.syntheticValue && (
                    <div className="macro-expansion-preview">\u2192 {macro.syntheticValue}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Runtime Stats */}
        <div className="runtime-stats-section">
          <h2>Runtime Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-number">{networkRequests.length}</span>
              <span className="stat-desc">Total Requests</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{allowedCount}</span>
              <span className="stat-desc">Allowed</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{blockedCount}</span>
              <span className="stat-desc">Blocked</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{(totalBytes / 1024).toFixed(1)} KB</span>
              <span className="stat-desc">Transferred</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{report.runtimeTrace?.navigationAttempts || 0}</span>
              <span className="stat-desc">Navigations</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{report.runtimeTrace?.popupAttempts || 0}</span>
              <span className="stat-desc">Popups</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{cookiesSet.length}</span>
              <span className="stat-desc">Cookies Set</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{storageAccess.length}</span>
              <span className="stat-desc">Storage Writes</span>
            </div>
          </div>
        </div>

        {/* Cookies Detail */}
        {cookiesSet.length > 0 && (
          <div className="network-section">
            <h2>Cookies Set ({cookiesSet.length})</h2>
            <div className="network-table-wrapper">
              <table className="network-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Domain</th>
                    <th>Secure</th>
                    <th>HttpOnly</th>
                    <th>SameSite</th>
                  </tr>
                </thead>
                <tbody>
                  {cookiesSet.map((c, idx) => (
                    <tr key={idx}>
                      <td>{c.name}</td>
                      <td>{c.domain}</td>
                      <td>{c.secure ? '\u2713' : '\u2717'}</td>
                      <td>{c.httpOnly ? '\u2713' : '\u2717'}</td>
                      <td>{c.sameSite}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Extracted URLs */}
        {report.artifact.extractedUrls.length > 0 && (
          <div className="network-section">
            <h2>Extracted URLs</h2>
            <div className="network-table-wrapper">
              <table className="network-table">
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Host</th>
                    <th>Context</th>
                  </tr>
                </thead>
                <tbody>
                  {report.artifact.extractedUrls.map((u, idx) => (
                    <tr key={idx}>
                      <td className="url-cell">{u.url}</td>
                      <td>{u.host}</td>
                      <td>{u.sourceContext}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* VAST Analysis */}
        {report.vastAnalysis && (
          <div className="vast-section">
            <h2>VAST Analysis</h2>
            <div className="vast-content">
              {report.vastAnalysis.wrapperChain.length > 0 && (
                <div className="vast-subsection">
                  <h4>Wrapper Chain</h4>
                  <ol className="wrapper-chain">
                    {report.vastAnalysis.wrapperChain.map((url, idx) => (
                      <li key={idx}>{url}</li>
                    ))}
                  </ol>
                </div>
              )}
              {report.vastAnalysis.mediaFiles.length > 0 && (
                <div className="vast-subsection">
                  <h4>Media Files</h4>
                  <ul className="media-files">
                    {report.vastAnalysis.mediaFiles.map((mf, idx) => (
                      <li key={idx}>{mf.url} ({mf.type})</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="vast-flags">
                <span className={`flag ${report.vastAnalysis.vpaidDetected ? 'active' : ''}`}>VPAID</span>
                <span className={`flag ${report.vastAnalysis.simidDetected ? 'active' : ''}`}>SIMID</span>
                <span className={`flag ${report.vastAnalysis.omidDetected ? 'active' : ''}`}>OMID</span>
              </div>
            </div>
          </div>
        )}

        {/* Network Requests */}
        {networkRequests.length > 0 && (
          <div className="network-section">
            <h2>Network Requests ({networkRequests.length})</h2>
            <div className="network-table-wrapper">
              <table className="network-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>URL</th>
                    <th>Method</th>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {networkRequests.map((req, idx) => (
                    <tr key={idx} className={req.blocked ? 'row-blocked' : ''}>
                      <td>
                        <span className={`request-status ${req.blocked ? 'request-blocked' : 'request-allowed'}`}>
                          {req.blocked ? 'BLOCKED' : 'OK'}
                        </span>
                      </td>
                      <td className="url-cell">{req.url}</td>
                      <td>{req.method}</td>
                      <td className={`status-${Math.floor(req.status / 100)}`}>{req.status || '\u2014'}</td>
                      <td>{req.resourceType}</td>
                      <td>{req.contentLength ? (req.contentLength / 1024).toFixed(2) + ' KB' : '\u2014'}</td>
                      <td>{req.timing ? req.timing + 'ms' : '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Runtime Trace */}
        {(consoleMessages.length > 0 || runtimeErrors.length > 0) && (
          <div className="trace-section">
            <h2>Runtime Trace</h2>
            {runtimeErrors.length > 0 && (
              <div className="trace-subsection">
                <h4>Errors ({runtimeErrors.length})</h4>
                <div className="errors-container">
                  {runtimeErrors.map((err, idx) => (
                    <div key={idx} className="error-entry">{err}</div>
                  ))}
                </div>
              </div>
            )}
            {consoleMessages.length > 0 && (
              <div className="trace-subsection">
                <h4>Console Messages ({consoleMessages.length})</h4>
                <div className="logs-container">
                  {consoleMessages.map((log, idx) => (
                    <div key={idx} className={`log-entry log-${log.level}`}>
                      [{log.level}] {log.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Redirect Chain */}
        {report.runtimeTrace?.redirectChain && report.runtimeTrace.redirectChain.length > 0 && (
          <div className="findings-section">
            <h2>Redirect Chain ({report.runtimeTrace.redirectChain.length})</h2>
            <ol className="wrapper-chain">
              {report.runtimeTrace.redirectChain.map((url, idx) => (
                <li key={idx}>{url}</li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
